import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { homepageContent, testimonials } from '../../data/products';
import Icon from '../Icon/Icon';
import { useContent } from '../../context/ContentContext';
import { Reveal } from '../../motion';
import './Testimonials.css';

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 90 : -90, opacity: 0 }),
  center: {
    x: 0, opacity: 1,
    transition: { type: 'spring', stiffness: 300, damping: 30 }
  },
  exit: (dir) => ({
    x: dir > 0 ? -90 : 90, opacity: 0,
    transition: { duration: 0.25, ease: 'easeIn' }
  })
};

const Testimonials = () => {
  const { revision } = useContent();
  const [[active, dir], setSlide] = useState([0, 1]);
  const [paused, setPaused] = useState(false);
  const reduce = useReducedMotion();
  const dotsRef = useRef(null);

  const go = (next, d) =>
    setSlide([(next + testimonials.length) % testimonials.length, d]);

  useEffect(() => {
    // Reduced-motion users get no auto-advance at all — MotionConfig only
    // neutralises the transform, the timer would still swap the copy on them.
    if (paused || reduce || testimonials.length < 2) return;
    const timer = setInterval(
      () => setSlide(([a]) => [(a + 1) % testimonials.length, 1]),
      homepageContent.testimonials.autoAdvanceMs
    );
    return () => clearInterval(timer);
  }, [paused, reduce, revision]);

  // Roving-tabindex arrow keys across the dot tablist
  const onDotKeyDown = (e) => {
    let next = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = active + 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = active - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = testimonials.length - 1;
    if (next === null) return;
    e.preventDefault();
    const i = (next + testimonials.length) % testimonials.length;
    go(i, i > active ? 1 : -1);
    const dot = dotsRef.current?.querySelectorAll('.dot')[i];
    if (dot) dot.focus();
  };

  useEffect(() => { setSlide(([index, direction]) => [testimonials.length ? index % testimonials.length : 0, direction]); }, [revision]);
  const t = testimonials[active] || testimonials[0];

  if (!t) return null;

  return (
    <section className="testimonials">
      <Reveal className="testimonials-inner">
        <div className="section-tag">{homepageContent.testimonials.eyebrow}</div>
        <h2 className="section-title">{homepageContent.testimonials.title}</h2>

        {/* One hover/focus region so the dots pause the timer too */}
        <div
          className="carousel-region"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
        >
          <div
            className="testimonials-carousel"
            id="testimonials-panel"
            role="tabpanel"
            aria-labelledby={`testimonial-tab-${active}`}
            aria-live="polite"
            aria-atomic="true"
          >
            <AnimatePresence custom={dir} initial={false}>
              <motion.div
                key={active}
                className="testimonial-card"
                custom={dir}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.55}
                onDragStart={() => setPaused(true)}
                onDragEnd={(e, info) => {
                  setPaused(false);
                  if (info.offset.x < -60 || info.velocity.x < -400) go(active + 1, 1);
                  else if (info.offset.x > 60 || info.velocity.x > 400) go(active - 1, -1);
                }}
              >
                <div className="stars">
                  {Array.from({ length: t.rating }).map((_, s) => (
                    <span key={s}>
                      <Icon name="star" size={14} strokeWidth={1.2} className="star-filled" />
                    </span>
                  ))}
                </div>
                <p className="testimonial-text">"{t.text}"</p>
                <div className="testimonial-author">
                  <div className="author-avatar">{t.name[0]}</div>
                  <div>
                    <div className="author-name">{t.name}</div>
                    <div className="author-location"><Icon name="pin" size={14} /> {t.location}</div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="carousel-controls">
            <div
              className="carousel-dots"
              role="tablist"
              aria-label="Customer stories"
              ref={dotsRef}
              onKeyDown={onDotKeyDown}
            >
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  id={`testimonial-tab-${i}`}
                  className={`dot ${i === active ? 'active' : ''}`}
                  role="tab"
                  aria-selected={i === active}
                  aria-controls="testimonials-panel"
                  tabIndex={i === active ? 0 : -1}
                  onClick={() => go(i, i > active ? 1 : -1)}
                  aria-label={`Go to testimonial ${i + 1}`}
                />
              ))}
            </div>

            {/* WCAG 2.2.2 — a real stop control, not just hover */}
            {!reduce && (
              <button
                type="button"
                className="carousel-toggle"
                onClick={() => setPaused((p) => !p)}
                aria-pressed={paused}
                aria-label={paused ? 'Play customer stories' : 'Pause customer stories'}
              >
                <svg
                  width="16" height="16" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="1.6"
                  strokeLinecap="round" strokeLinejoin="round"
                  aria-hidden="true"
                >
                  {paused
                    ? <path d="M8 5.5 18 12 8 18.5V5.5Z" />
                    : <><path d="M9.5 5.5v13" /><path d="M14.5 5.5v13" /></>}
                </svg>
              </button>
            )}
          </div>
        </div>

        <p className="swipe-hint" aria-hidden="true">Swipe or hover to pause</p>
      </Reveal>
    </section>
  );
};
export default Testimonials;
